/**
 * 山陰と西端の国データ
 *
 * 担当: 但馬(28) / 出雲(29) / 石見(30) / 長門(32)
 *
 * 石高ランクと防御は docs/01-ゲームデザイン.md §2.2 の盤上バランス値。
 * historicalKokudaka は慶長3年(1598)の国別石高（万石）を用い、
 * 盤上の値が調整済みであることを各国の gameNote に明記している。
 *
 * 執筆基準は _reference.ts（尾張・甲斐）と docs/05-学習設計.md §4 に従う。
 *
 *   28 但馬 … 山国。地の利は弓。生野銀山で銭を産む
 *   29 出雲 … 出雲平野を採って平野。地の利は騎馬
 *   30 石見 … 山国。地の利は弓。石見銀山で銭を産む
 *   32 長門 … 湊の国。地の利は鉄砲（第I章では足軽に読み替え）
 *
 * region は令制の道に従うため、長門のみ 'sanyo' としている。
 */

import type { Province } from '../types';

/** 但馬。山国（弓）。生野銀山を持つ小国で、守護山名氏の本国。 */
export const TAJIMA: Province = {
  id: 28,
  kind: 'province',
  name: '但馬',
  reading: 'たじま',
  region: 'sanin',
  kokudaka: 1,
  defense: 2,
  terrain: 'mountain',
  historicalKokudaka: 11.4,
  yields: [{ res: 'zeni', n: 1 }],
  note: {
    summary:
      '日本海に面した山がちの国。円山川の谷に小さな盆地が連なり、生野銀山の銀が国の富となった。守護山名氏の本国。',
    detail: {
      1: 'いまの兵庫県の北がわにあった国です。山が多く、日本海に面しています。生野というところで銀がとれたので、その銀が国のたからになりました。山名という一族が長いあいだこの国をおさめていました。',
      2: '現在の兵庫県北部にあたる国。国土の大半が山地で、円山川とその支流が刻む豊岡・出石・養父などの小さな盆地に耕地が限られる。米の生産は多くないが、1542年に守護の山名祐豊が生野に城を構えて銀山の開発を進め、石見銀山から灰吹法という精錬の技術を取り入れたと伝えられる。銀は国の重要な財源になった。山名氏は応仁の乱で西軍を率いた山名宗全（持豊）を出した家だが、16世紀後半には勢力が衰え、1569年に此隅山城を織田方に落とされ、1580年には有子山城が開城して但馬の支配を終えた。',
      3: '現在の兵庫県北部。慶長3年(1598)の石高は約11.4万石で、全国では小さい部類に入る。中国山地の北縁にあたり、円山川水系が刻む豊岡・出石・養父などの盆地に耕地が散在する一方、日本海に面した城崎・津居山などの浦は北国海運の寄港地となった。城崎の湯は中世から湯治の場として知られ、温泉寺の門前に人が集まっている。国の性格を大きく決めたのは鉱山で、生野銀山は1542年に但馬守護山名祐豊が生野城を構えて本格的な採掘に入ったと伝えられ、石見から灰吹法が導入されて産出が伸びた。鉱山には各地から掘子や商人が集まり、山あいに町が生まれている。銀山は織田・豊臣・徳川と支配者が代わるたびに直轄地とされた。ほかに明延の錫・銅など鉱産資源が広く分布する。山名氏は応仁の乱で西軍の中心となった山名宗全を出した家で、但馬を代々の本国としたが、16世紀後半には家中の分裂と近隣勢力の圧迫で衰えた。1569年に此隅山城を落とされた祐豊は堺の商人の助けで復帰し有子山城を築いたが、1580年に羽柴秀長の攻撃を受けて城は開かれ、祐豊はまもなく病没している。以後の但馬は豊臣政権の支配下に入り、竹田城には赤松広秀が置かれた。',
    },
    year: [1467, 1600],
    keywords: ['但馬', '山名氏', '生野銀山', '円山川', '出石', '灰吹法'],
    caveat:
      '生野銀山の開坑を大同2年(807年)とする言い伝えがありますが、これを裏づける同時代の記録はありません。1542年の山名祐豊による開発も後世の記録に基づく部分が大きく、当時の産出量ははっきりしていません。竹田城を「天空の城」と呼ぶのは近年広まった言い方で、当時の名称ではありません。',
    gameNote:
      '盤上の石高ランク1と防御値2は遊びやすさのために調整した相対値です。実際の但馬は慶長3年の石高で約11.4万石でした。銭の産出は生野銀山を置き換えたものです。地形は山国（弓）としていますが、実際の但馬は日本海に面しており、円山川河口の浦は海運の拠点でもありました。',
  },
};

/** 出雲。平野（騎馬）。尼子氏が月山富田城に拠り、杵築大社とたたら製鉄で知られる。 */
export const IZUMO: Province = {
  id: 29,
  kind: 'province',
  name: '出雲',
  reading: 'いずも',
  region: 'sanin',
  kokudaka: 2,
  defense: 3,
  terrain: 'plain',
  historicalKokudaka: 18.7,
  note: {
    summary:
      '山陰の中心となった国。斐伊川がつくる平野と宍道湖を抱え、杵築大社の門前と山あいのたたら製鉄で知られる。尼子氏が月山富田城に拠った。',
    detail: {
      1: 'いまの島根県の東がわにあった国です。大きな湖（宍道湖）と、川がはこんだ土でできた平野があります。山では砂鉄から鉄をつくっていました。出雲大社があることでも有名で、尼子という一族が山の上の城をかまえていました。',
      2: '現在の島根県東部にあたる国。斐伊川がつくる出雲平野と宍道湖・中海を抱え、山陰では耕地が広い方に入る。南の中国山地では砂鉄を原料とする「たたら製鉄」が行われ、鉄が特産となった。日本海に面した美保関は北国海運の寄港地で、杵築大社（現在の出雲大社）の門前にも各地から人が集まっている。守護は京極氏だったが、守護代の尼子経久が実権を握って自立し、月山富田城を拠点に山陰・山陽へ勢力を広げた。1542年から翌年にかけての大内義隆の攻撃は退けたものの、1566年に毛利元就の攻囲を受けて月山富田城は開城し、尼子氏の支配は終わった。',
      3: '現在の島根県東部。慶長3年(1598)の石高は約18.7万石で、山陰では最大級にあたる。斐伊川と神戸川が運ぶ土砂が出雲平野を広げ、宍道湖・中海と結ぶ水域が内陸の水運を支えた。中国山地では砂鉄を採る鉄穴（かんな）流しとたたら製鉄が広く行われ、鉄は出雲を代表する産物になる。ただし鉄穴流しが押し流した大量の土砂は河床を上げ、斐伊川の流路が変わる一因になったとされ、生産と水害が背中合わせだった。信仰の面では杵築大社（現在の出雲大社）が中心で、社殿の造営費を集めるために各地を勧進して回る人々があり、そのなかには女性の芸能者も含まれた。1603年に京でかぶき踊りを演じ、同時代の記録『当代記』に「出雲國神子女、名ハ國」と書かれた出雲阿国も、この流れのなかに位置づけられている。政治的には、室町幕府の守護は京極氏だが、守護代の尼子経久が16世紀初めに自立し、孫の晴久の代には山陰・山陽の広い範囲に影響力を及ぼした。1542年から翌年の大内義隆による月山富田城攻めは退けたが、1560年代に入ると毛利元就の圧迫を受け、1565年から続いた兵糧攻めの末、1566年11月に城を開いて尼子義久は安芸へ移された。その後も尼子勝久・山中幸盛らによる再興の試みが繰り返されている。',
    },
    year: [1467, 1600],
    keywords: ['出雲', '尼子氏', '月山富田城', '杵築大社', 'たたら製鉄', '宍道湖', '斐伊川'],
    caveat:
      '「出雲大社」という呼び名が一般化するのは近世以降で、中世の史料では杵築大社（きづきたいしゃ）と記されます。出雲阿国については、出雲大社の巫女を名乗ったと伝えられるものの、出自や生涯を確かめられる史料はほとんど残っていません。たたら製鉄を古代から一貫して大規模に行われたものとする説明もよく見られますが、生産が飛躍的に拡大するのは近世に入ってからで、中世の規模ははっきりしていません。',
    gameNote:
      '盤上の石高ランク2と防御値3は遊びやすさのために調整した相対値です。実際の出雲は慶長3年の石高で約18.7万石でした。地形は出雲平野を採って平野（騎馬）としていますが、実際の出雲は南半分が中国山地で、日本海側には美保関などの湊もあります。防御値3は盤上の釣り合いで決めた数字であり、月山富田城が実際に落ちにくかったこと（大内義隆の攻撃を退け、毛利元就も1年以上を要した）を数値化したものではありません。',
  },
};

/** 石見。山国（弓）。石見銀山を持ち、盤上で最も銭を産む国。 */
export const IWAMI: Province = {
  id: 30,
  kind: 'province',
  name: '石見',
  reading: 'いわみ',
  region: 'sanin',
  kokudaka: 2,
  defense: 3,
  terrain: 'mountain',
  historicalKokudaka: 11.2,
  yields: [{ res: 'zeni', n: 2 }],
  note: {
    summary:
      '山が海に迫る細長い国。1520年代に開かれた石見銀山の銀が東アジアの交易に流れ込み、大内・尼子・毛利が銀山をめぐって争った。',
    detail: {
      1: 'いまの島根県の西がわにあった国です。山がすぐ海までせまっていて、たいらな土地はわずかしかありません。けれども銀のとれる山があり、その銀は船で外国まではこばれました。銀山を手に入れるため、まわりの大名が何度も戦いました。',
      2: '現在の島根県西部にあたる国。中国山地が日本海に迫り、江の川などの川沿いにわずかな平地があるだけで、石高は多くない。しかし1526年に博多の商人・神屋寿禎が銀の鉱脈を見つけたと伝えられ、1533年に灰吹法という精錬技術が持ち込まれてから産出が急に増えた。銀は鞆ケ浦や温泉津の湊から積み出され、明や朝鮮との取引に使われている。銀山の支配をめぐって周防の大内氏、出雲の尼子氏、安芸の毛利氏が争い、1562年ごろに毛利氏が押さえた。銀山では掘子（ほりこ）と呼ばれる働き手が坑内に入り、粉じんによる病も知られていた。',
      3: '現在の島根県西部。慶長3年(1598)の石高は約11.2万石にとどまり、中国山地が日本海に迫る地形のため耕地が乏しい。国の比重は圧倒的に鉱山にあった。石見銀山（大森銀山）は、1526年に博多商人の神屋寿禎が銀峯山の露頭を見つけて採掘を始めたと伝えられ、1533年に寿禎が博多から宗丹・桂寿という技術者を招いて灰吹法を導入したことで、鉱石から効率よく銀を取り出せるようになった。以後の産出量は飛躍的に伸び、銀は鞆ケ浦・沖泊・温泉津の湊から積み出されて、明・朝鮮、のちにはポルトガル商人を介した東アジアの交易へ流れ込む。16世紀から17世紀前半の日本銀が世界の銀産出のうちかなりの割合を占めたことは広く指摘されており、石見はその中心的な産地だった。銀山は周防の大内氏、出雲の尼子氏、安芸の毛利氏による争奪の的となり、山吹城をめぐる攻防を経て1562年前後に毛利氏が支配を固めている。鉱山町には掘子・製錬職人・商人とその家族が集まって人口が集中し、寺社や市が営まれた。坑内の粉じんによる肺の病が知られ、働き手の寿命は短かったと伝えられる。2007年に「石見銀山遺跡とその文化的景観」として世界遺産に登録された。',
    },
    year: [1526, 1600],
    keywords: ['石見', '石見銀山', '神屋寿禎', '灰吹法', '温泉津', '毛利氏', '大内氏'],
    caveat:
      '神屋寿禎による1526年の発見と1533年の灰吹法導入は、江戸時代前期に編まれた『銀山旧記』などに拠るもので、同時代の史料で細部まで確かめられるわけではありません。寿禎が招いた宗丹・桂寿は博多から来たと伝えられますが、灰吹法という技術そのものの伝来経路については中国由来説と朝鮮由来説があり、定説はありません。「世界の銀の3分の1が日本産だった」という言い方も推計であり、研究者によって3分の1から4分の1程度まで幅があります。また、これは日本全体の銀についての推計で、石見銀山ひとつで世界の3分の1を産したという説明は誤りです。',
    gameNote:
      '盤上の石高ランク2と防御値3は遊びやすさのために調整した相対値です。実際の石見は慶長3年の石高で約11.2万石で、米の量だけでいえば盤上のランク2よりも小さい国でした。銀山の価値は石高ではなく yields（銭の産出）の側に置いています。地形は山国（弓）としており、これは銀山が中国山地の中にあったことにも合っています。',
  },
};

/** 長門。湊（鉄砲／第I章では足軽）。本州の西端で、赤間関が海路の結節点だった。 */
export const NAGATO: Province = {
  id: 32,
  kind: 'province',
  name: '長門',
  reading: 'ながと',
  region: 'sanyo',
  kokudaka: 2,
  defense: 2,
  terrain: 'coast',
  historicalKokudaka: 13.1,
  note: {
    summary:
      '本州の西端で、日本海と瀬戸内海が出会う国。赤間関が海上交通と対外交易の結節点となり、大内氏、のちに毛利氏が領した。',
    detail: {
      1: 'いまの山口県の西がわにあった国です。本州のいちばん西のはしにあり、日本海と瀬戸内海が出あう海峡に面しています。赤間関（いまの下関）という港に、たくさんの船が集まりました。大内氏がおさめ、そのあとは毛利氏の国になりました。',
      2: '現在の山口県西部にあたる国。関門海峡に面した赤間関（現在の下関）は、日本海と瀬戸内海を行き来する船が必ず通る要地で、大内氏は代官を置いて関銭を取り、明や朝鮮の使節への応対にもあたった。室町幕府の遣明船も往復のたびにここへ寄っている。大内氏は隣国周防の山口を本拠とし、周防・長門の二か国を基盤に日明貿易を握って栄えた。1551年、家臣の陶隆房（晴賢）が背いて大内義隆は長門の大寧寺で自害し、1557年には大内義長が長府の長福寺（現在の功山寺）で自害して大内氏は絶えた。以後、長門は毛利氏の領国となる。',
      3: '現在の山口県西部。慶長3年(1598)の石高は約13.1万石。国の性格を決めたのは海で、本州西端の関門海峡に面する赤間関（下関）は、瀬戸内海と日本海を結ぶ航路の結節点だった。中関・上関と合わせて防長三関と呼ばれ、大内氏は赤間関に代官を置いて関銭・帆別銭を徴収し、明・朝鮮の使節の応接にもあたっている。遣明船は往復ともここに寄港し、帰国時には赤間関から京へ使者が立てられた。阿弥陀寺の門前には町屋が並び、船乗り・問屋・職人とその家族が暮らす港町が早くから形づくられている。大内氏は周防の山口を本拠として周防・長門を領し、1523年の寧波の乱を経て日明貿易をほぼ独占した。応仁の乱で荒れた京から公家や僧を迎え入れ、山口は「西の京」と呼ばれる文化の集積地になっている。1551年、重臣の陶隆房（晴賢）が挙兵し、大内義隆は長門深川の大寧寺で自害した（大寧寺の変）。陶氏が擁立した大内義長も、1555年の厳島の戦いで陶晴賢が敗死したのちに追い詰められ、1557年に長門長府の長福寺（現在の功山寺）で自害して大内氏は滅んだ。長門は毛利氏の領国となり、1600年の関ヶ原ののち毛利氏は周防・長門の二か国に減らされて、長門の萩へ本拠を移すことになる。海に開かれた国であることは変わらず、赤間関は近世に北前船の寄港地としてさらに賑わった。',
    },
    year: [1467, 1600],
    keywords: ['長門', '赤間関', '大内氏', '毛利氏', '関門海峡', '大寧寺の変', '日明貿易'],
    caveat:
      'フランシスコ・ザビエルが布教の許可を得た山口は隣国の周防国であって長門ではありません。長門の出来事として語られることがありますが、正確ではありません。大内氏を百済の王族の子孫とする系譜も大内氏自身が主張したもので、史実として確かめられてはいません。赤間関を「馬関」と呼ぶのは近世以降の用法です。',
    gameNote:
      '盤上の石高ランク2と防御値2は遊びやすさのために調整した相対値です。実際の長門は慶長3年の石高で約13.1万石でした。地形は湊（coast）としています。長門の内陸は山がちですが、国の性格としては関門海峡と日本海・瀬戸内の海運の比重が大きいと判断しました。なお長門は令制では山陽道の国なので region は sanyo としていますが、盤上では山陰の国々に続く位置に置かれています。',
  },
};
